<template lang="pug">
    section.purchase.purchase-on-hold(:class="{ active: purchase.id == cartPurchaseID }")
        .head
            .summary
                .left
                    nobr.nr {{ purchase.id }}
                    nobr.name {{ purchase.purchaser.name }}
                    br
                    nobr.date
                        small {{ purchase.created_at | timestamp2date(dateTimeFormat) }}
                .right
                    nobr.grand-total {{ purchase.grand_total | moneyFormat(purchase.currency) }}
            .capture
                nobr
                    el-button(
                        size="small"
                        type="success"
                        icon="el-icon-fa-shopping-cart fas"
                        :disabled="purchase.id == cartPurchaseID"
                        @click="loadPurchase"
                    ) {{ $t('Sell') }}
                    el-button.cancel-button(
                        size="small"
                        type="danger"
                        icon="el-icon-fa-ban fas"
                        :loading="canceling"
                        @click="cancelPurchaseModalVisibility = true"
                    ) {{ $t('Cancel') }}
        .items
            .item(v-for="item in purchase.items")
                item-media-preview(:item="item")
                .info
                    .row
                        nobr.nr {{ item.product_nr }}
                        nobr.name {{ item.name }}
                        nobr.price {{ item.sale_price || item.price | moneyFormat(purchase.currency) }}
                    .row.properties
                        property-value(
                            v-for="property in item.selectedProperties"
                            :key="property.id"
                            :property="property"
                        )
                    .row
                        nobr.sku
                            label {{ $t('SKU') }}:
                            span.value {{ item.sku }}
                        nobr.stock-not-managed(v-if="!item.manage_stock")
                            label {{ $t('Stock is not managed') }}:
                        nobr.not-available(v-else-if="getAvailableQty(item) < item.qty")
                            label {{ $t('Available') }}:
                            span.value {{ getAvailableQty(item) }}
                        nobr.qty
                            label {{ $t('On hold quantity') }}:
                            span.value {{ item.qty }}

        el-dialog.cancel-purchase-dialog(
            :title="$t('Are you sure you want to cancel this purchase?')"
            :visible.sync="cancelPurchaseModalVisibility"
            :close-on-press-escape="false"
            :close-on-click-modal="false"
            top="10vh"
            width="460px"
            @closed="resetCancelPurchaseModal"
        )
            el-form.property-modal-form(
                ref="cancelPurchaseForm"
                :model="cancelPurchaseForm"
                label-position="top"
                :rules="cancelPurchaseRules"
                v-loading="cancelPurchaseModalLoading"
                @submit.prevent.native
            )
                transition(name="slide-vertical")
                    .modal-body(v-if="! cancelPurchaseModalLoading")
                        //- el-alert(
                        //-     type="info"
                        //-     :title="$t('Stock')"
                        //-     :description="$t('Stock is going back to the corresponding warehouse')"
                        //-     :closable="false"
                        //-     show-icon
                        //- )
                        .alert.info
                            i.el-icon-info
                            .text
                                b {{ $t('Stock management') }}
                                p {{ $t('Stock is going back to the corresponding warehouse') }}
            template(slot="footer")
                el-button(
                    size="mini"
                    icon="el-icon-fa-times fas"
                    @click="closeCancelPurchaseModal"
                ) {{ $t('Dismiss') }}
                el-button(
                    :disabled="cancelPurchaseModalLoading"
                    size="mini"
                    type="danger"
                    icon="el-icon-fa-check fas"
                    @click="cancelPurchase"
                ) {{ $t('Cancel irrevocably') }}
</template>

<i18n>
{
    "de": {
        "SKU": "SKU",
        "On hold quantity": "Zurückgelegt",
        "Sell": "Verkaufen",
        "Available": "Verfügbar",
        "Stock is not managed": "Lager wird nicht verwaltet",
        "Cancel": "Stornieren",
        "Are you sure you want to cancel this purchase?": "Soll diese Bestellung wirklich storniert werden?",
        "Cancel irrevocably": "Unwiderruflich stornieren",
        "Purchase canceled successfully!": "Bestellung erfolgreich storniert!",
        "Purchase could not be canceled.": "Bestellung konnte nicht storniert werden.",
        "Stock management": "Lagerverwaltung",
        "Stock is going back to the corresponding warehouse": "Der Bestand geht zurück in das entsprechende Lager"
    }
}
</i18n>

<script>
// import _ from 'lodash'
// import uniqid from 'uniqid'
//
// import Fuse from 'fuse.js'
import { mapMutations, mapActions, mapGetters } from 'vuex'

// import { differenceInMinutes, isPast } from 'date-fns'
// import PurchaseApi from '@/extensions/shop/store/api/purchase'
// import AuthApi from '@/store/api/system/auth'
//
// import { country } from '@/filters'
// import { fi } from 'date-fns/locale'

import PurchaseApi from '@/extensions/shop/store/api/purchase';

import PropertyValue from '@/extensions/shop/views/Purchase/Form/Partials/PropertyValue'
import ItemMediaPreview from '@/extensions/shop/pointOfSale/views/Partials/ItemMediaPreview'

import { timestamp2date } from '@/filters'

export default {
    name: 'PurchaseOnHold',
    props: {
        purchase: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            canceling: false,

            cancelPurchaseModalVisibility: false,
            cancelPurchaseModalLoading: false,
            cancelPurchaseForm: {},
            cancelPurchaseRules: {},
        };
    },
    components: {
        PropertyValue,
        ItemMediaPreview,
    },
    filters: {
        timestamp2date,
    },
    computed: {
        dateTimeFormat() {
            const template = this.$t('yyyy-MM-dd')
            return `${template}, HH:mm`
        },
        ...mapGetters({
            options: 'ext_pos_setup/options',
            activePointOfSale: 'ext_pos_setup/activePointOfSale',
            cartPurchaseID: 'ext_pos_cart/id',
            cartPurchaser: 'ext_pos_cart/purchaser',
        })
    },
    methods: {
        loadPurchase() {
            // set purchase id
            // set purchaser
            // add items to cart
            // update item custom prices
            // set coupons

            const purchaser = this.purchase.purchaser

            if (!this.cartPurchaseID || this.cartPurchaseID.startsWith('new:')) {
                this.setPurchaseId(this.purchase.id)
                this.setCoupons()

                this.setPurchaser(purchaser)
                this.initializeCustomerCoupons()
                this.fetchPurchasesOnHold()
            } else if (purchaser.customer.id != _.get(this.cartPurchaser, 'customer.id')) {
                console.error('customer mismatch')
                return
            }

            this.purchase.items.forEach(item => {
                const article = item.article
                if (!article || !article.product) return

                let selectedPropertyPivots = []
                if (item.selectedProperties && item.selectedProperties.length) {
                    const propertyPivots = article.product.propertyPivots
                    item.selectedProperties.forEach(el => {
                        const key = el.key
                        const propertyPivot = propertyPivots.find(pivot => pivot.property.key === key)
                        if (!propertyPivot) return

                        const valueOption = propertyPivot.valueOptions.find(option => option.id == el.value.id)
                        if (!valueOption) return

                        selectedPropertyPivots.push({
                            property: propertyPivot.property,
                            valueOption,
                        })
                    })

                    if (selectedPropertyPivots.length !== item.selectedProperties.length) {
                        console.error('could not find all selected properties')
                        return
                    }
                }

                let payload = {
                    article,
                    selectedPropertyPivots,
                    qty: item.qty,
                    salePrice: item.sale_price,
                }

                if (!this.cartPurchaseID || this.cartPurchaseID.startsWith('new:')) {
                    payload.id = item.id
                }

                this.addArticleToCart(payload)
            })

            this.purchase.coupons.forEach(coupon => {
                this.addCoupon(coupon)
            })

            this.saveCart()
        },
        getRelevantArticleStocks(article) {
            const stocks = article.stocks || []

            const stockIds = this.activePointOfSale.stocks.map(e => e.id)
            const posStocks = stocks.filter(el => stockIds.includes(el.id))

            const stockIDs = posStocks.map(el => el.id)
            return stocks.filter(el => stockIDs.includes(el.id))
        },
        getAvailableQty(item) {
            let availableQty = 0;

            if (item.article && item.manage_stock) {
                const stocks = this.getRelevantArticleStocks(item.article)
                availableQty = stocks.reduce((pv, relatedStock) => {
                    const onHoldStock = item.stocks.find(el => el.id == relatedStock.id)
                    return pv + _.get(relatedStock, 'pivot.qty', 0) + _.get(onHoldStock, 'pivot.qty', 0)
                }, 0)
            }

            return availableQty;
        },
        resetCancelPurchaseModal() {
            this.$refs.cancelPurchaseForm.resetFields();
        },
        closeCancelPurchaseModal() {
            this.cancelPurchaseModalVisibility = false;
        },
        async cancelPurchase() {
            const id = this.purchase.id
            const status = this.options.status.items.find(el => el.key === 'canceled');
            if (!id || !status) {
                console.error('id or status not found')
                return
            }

            this.closeCancelPurchaseModal();
            this.canceling = true;

            await PurchaseApi.update({ id, status })
                .then(() => {
                    let notification = {
                        title: this.$t('Purchase canceled successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    };
                    this.$notify.warning(notification);

                    if (this.cartPurchaseID && this.cartPurchaseID == id) {
                        this.setPurchaseId(undefined)
                    }
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);

                    let notification = {
                        title: this.$t('An error occurred!'),
                        message: this.$t('Purchase could not be canceled.'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    };
                    this.$notify.error(notification);
                });

            this.canceling = false;
        },
        ...mapMutations({
            addArticleToCart: 'ext_pos_cart/addItem',
            // updateAutofocusSearchInput: 'ext_pos_setup/updateAutofocusSearchInput',
            // updateLoading: 'ext_pos_setup/updateLoading',
            // updateItemQtyMutation: 'ext_pos_cart/updateItemQty',
            // updateItemQuantities: 'ext_pos_cart/updateItemQuantities',
            // updateArticleStocks: 'ext_pos_cart/updateArticleStocks',
            // deleteItemMutation: 'ext_pos_cart/deleteItem',
            // updateItem: 'ext_pos_cart/updateItem',
            setPurchaseId: 'ext_pos_cart/setId',
            setPurchaser: 'ext_pos_cart/setPurchaser',
            setCoupons: 'ext_pos_cart/setCoupons',
            addCoupon: 'ext_pos_cart/addCoupon',
        }),
        ...mapActions({
            initializeCustomerCoupons: 'ext_pos_cart/initializeCustomerCoupons',
            fetchPurchasesOnHold: 'ext_pos_setup/fetchPurchasesOnHold',
            saveCart: 'ext_pos_cart/save',
        })
    },
}
</script>

<style lang="sass" scoped>
.purchase-on-hold

    // &.active
    //     opacity: 0.5
    //     pointer-events: none

    label + span.value:before
        content: ' '

    label
        font-style: italic
        &.small
            font-size: 0.8em

    .head
        height: 48px
        display: flex
        gap: 1rem
        align-items: center
        // padding: 6px 12px
        padding: 6px 0
        margin-bottom: 0.5rem
        .summary
            flex-grow: 1
            // background: rgba(140, 147, 157, 0.2)
            // border-radius: 1rem
            display: flex
            justify-content: space-between
            align-items: center
            gap: 1rem
            .name
                margin-left: 1rem
            .grand-total
                text-align: right
                min-width: 80px

    .items
        gap: 1rem
        padding: 6px 12px
        padding-left: 6px
        // background: rgb(220, 221, 223, 0.5)
        background: rgba(255,255,255, 0.4)
        // border-radius: 1rem
        border-radius: 4px
        box-shadow: 0 2px 2px 0 rgba(0,0,0,0.04), 0 3px 1px -2px rgba(0,0,0,0.02), 0 1px 5px 0 rgba(0,0,0,0.1)
        & + .stock-item
            margin-top: 0.5rem
        & > .info
            display: flex
            flex-direction: column
            & > .row.properties
                flex-grow: 1

    .item
        display: flex
        gap: 12px
        label
            font-weight: bold
        .info
            flex-grow: 1
            min-width: 0
            padding: 6px 0
        .row
            display: flex
            gap: 0.5rem
            & + .row
                margin-top: 0.25rem
        .name
            flex-grow: 1
            min-width: 0
            white-space: nowrap
            overflow: hidden
            text-overflow: ellipsis
        .sku
            flex-grow: 1

    .item + .item
        margin-top: 0.5rem
        border-top: 1px solid rgba(0,0,0,0.1)
        padding-top: 0.5rem

.purchase-on-hold + .purchase-on-hold
    margin-top: 2rem

.modal-body
    .alert
        display: flex
        align-items: center
        margin: 0
        i
            opacity: 0.6
            min-width: 2rem
            font-size: 1.4rem
</style>
